import StatusChip from 'components/@extended/StatusChip';
import MatterIndicators, { BOTTOM_LINE_IN_NEW_DESIGN } from 'components/matters/MatterIndicators';
import { SearchTermHighlight } from 'components/SearchTermHighlight';
import { FormattedDate } from 'components/misc/FormattedDate';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Tooltip } from 'components/ui/tooltip';
import { getLinks, getStatusChipType } from 'pages/matters/util';
import LabelledData from 'pages/timecards/LabelledData';
import { ClassifierData } from 'types/classifiers';
import { CoiCheckDetailsJson, PartyDto, VisibilityScope } from 'types/coi-check';
import { MatterPartyRoleType } from 'types/matters';
import { NamedEntityMinDto } from 'types/other';
import { getClassifierText, t } from 'utils/getTranslations';
import { ENTITY_LINK_CLASS } from '../util/CoiCheckUtil';

export const MatterOpeningCell = ({
    detailsJson: value,
    classifiers,
}: {
    detailsJson: CoiCheckDetailsJson;
    classifiers: Record<string, ClassifierData>;
}): JSX.Element => {
    const isNewDesign = useIsNewDesign();
    const employees = value.employees || [];
    const statusChip = (
        <StatusChip
            className="self-start text-xs"
            label={getClassifierText(classifiers['MATTER_STATE_TYPE'], value.state)}
            type={getStatusChipType(value.state)}
        />
    );
    return value.visibility === VisibilityScope.FULL ? (
        <div className="flex flex-col gap-y-1">
            <span className="flex items-center gap-1.5 font-medium">
                {isNewDesign ? null : <MatterIndicators isConfidential={value.isConfidential} />}
                <RecordLink
                    withOrigin
                    className={ENTITY_LINK_CLASS}
                    to={`/matter/${value.id}`}
                    label={[value.number, value.name].filter(Boolean).join(' ')}
                >
                    {value.number ?? ''} <SearchTermHighlight text={value.name} />
                </RecordLink>
            </span>
            {isNewDesign ? null : statusChip}
            <div>{employees.length ? getLinks('employee', employees) : ''}</div>
            <span className="text-caption text-fg-secondary">
                <FormattedDate value={value.date} fallback="" />
            </span>
            {/* The new design keeps a list cell's chips on its bottom line. */}
            {isNewDesign ? (
                <div className="flex flex-wrap items-center gap-1">
                    <MatterIndicators isConfidential={value.isConfidential} />
                    {statusChip}
                </div>
            ) : null}
        </div>
    ) : (
        <div className="new:flex-wrap flex items-center gap-4">
            <MatterIndicators
                isConfidential={value.isConfidential}
                hasAccess={false}
                className={BOTTOM_LINE_IN_NEW_DESIGN}
            />
            <span className="text-caption text-fg-secondary font-medium">
                {value.number ?? ''} <SearchTermHighlight text={value.name} />
            </span>
        </div>
    );
};

export const MatterRelatedContactsCell = ({
    detailsJson: value,
}: {
    detailsJson: CoiCheckDetailsJson;
}): JSX.Element | null => {
    const clients = (value.parties || []).filter(
        (party: PartyDto) => party.matterPartyRoleTypeCode === MatterPartyRoleType.CLIENT_REPRESENTED
    );
    const targets = (value.parties || []).filter(
        (party: PartyDto) => party.matterPartyRoleTypeCode === MatterPartyRoleType.TARGET
    );
    const otherParties = (value.parties || []).filter(
        (party: PartyDto) => party.matterPartyRoleTypeCode === MatterPartyRoleType.OTHER_PARTY
    );
    const judges = (value.parties || []).filter(
        (party: PartyDto) => party.matterPartyRoleTypeCode === MatterPartyRoleType.JUDGE
    );

    return value?.visibility === VisibilityScope.FULL ? (
        <div className="flex flex-col">
            <LabelledData label="coi.client" className="text-body1">
                {value.matterClient ? getLinks('contact', [value.matterClient]) : ''}
            </LabelledData>
            <LabelledData label="coi.client-represented" className="text-body1">
                {clients.length ? getLinks('contact', clients) : ''}
            </LabelledData>
            <LabelledData label="coi.targets" className="text-body1">
                {targets.length ? getLinks('contact', targets) : ''}
            </LabelledData>
            <LabelledData label="coi.other-parties" className="text-body1">
                {otherParties.length ? getLinks('contact', otherParties) : ''}
            </LabelledData>
            <LabelledData label="coi.judges" className="text-body1">
                {judges.length ? getLinks('contact', judges) : ''}
            </LabelledData>
        </div>
    ) : null;
};

export const MatterDescriptionCell = ({ detailsJson: value }: { detailsJson: CoiCheckDetailsJson }): JSX.Element => {
    const isSingleEmployee = value.employees?.length === 1;
    return value?.visibility === VisibilityScope.FULL ? (
        <SearchTermHighlight text={value.description} />
    ) : (
        <div className="flex">
            <div className="flex-[2]" />
            <Tooltip
                content={t(isSingleEmployee ? 'coi.supervising-employee' : 'coi.supervising-responsible-employee')}
            >
                <div className="text-caption text-fg-secondary flex-1 cursor-default font-medium">
                    {(value.employees || []).map((employee: NamedEntityMinDto) => (
                        <div key={employee.id}>
                            <SearchTermHighlight text={employee.name} />
                        </div>
                    ))}
                </div>
            </Tooltip>
        </div>
    );
};
